import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useAuthStore } from '@/store/authStore'
import { getMyTrainers, Trainer } from '@/services/clients'
import { getUpcomingBookings, Booking } from '@/services/bookings'
import Button from '@/components/ui/Button'
import Avatar from '@/components/ui/Avatar'

export default function ClientDashboard() {
  const { user } = useAuthStore()
  const [trainer, setTrainer] = useState<Trainer | null>(null)
  const [pendingTrainer, setPendingTrainer] = useState<Trainer | null>(null)
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [trainersData, bookingsData] = await Promise.all([
        getMyTrainers(),
        getUpcomingBookings(),
      ])
      // Берём первого активного тренера (approved, trial или frozen - frozen может записаться)
      const activeTrainers = trainersData.trainers.filter(
        (t) => t.status === 'approved' || t.status === 'trial' || t.status === 'frozen'
      )
      setTrainer(activeTrainers.length > 0 ? activeTrainers[0] : null)

      // Проверяем есть ли pending тренеры
      const pendingTrainers = trainersData.trainers.filter(
        (t) => t.status === 'pending'
      )
      setPendingTrainer(pendingTrainers.length > 0 ? pendingTrainers[0] : null)

      setUpcomingBookings(bookingsData)
    } catch (err) {
      console.error('Error loading dashboard data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Refetch when app becomes visible (Telegram Mini App caching)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadData])

  const formatBookingDate = (booking: Booking) => {
    const date = parseISO(booking.slot.date)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
      return `Сегодня, ${booking.slot.start_time}`
    } else if (format(date, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd')) {
      return `Завтра, ${booking.slot.start_time}`
    }
    return `${format(date, 'd MMMM', { locale: ru })}, ${booking.slot.start_time}`
  }

  return (
    <div className="p-4">
      {/* Приветствие */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-tg-text">
          Привет, {user?.first_name}! 👋
        </h1>
        <p className="text-tg-hint mt-1">Ваши тренировки</p>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-tg-hint">Загрузка...</div>
      ) : !trainer && pendingTrainer ? (
        // Есть заявка, ожидаем подтверждения
        <div className="text-center py-8">
          <div className="text-6xl mb-4">⏳</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">
            Заявка отправлена
          </h2>
          <p className="text-tg-hint mb-6">
            Тренер {pendingTrainer.user.first_name} получил вашу заявку.
            <br />
            Ожидайте подтверждения.
          </p>
          <div className="bg-tg-secondary rounded-xl p-4 inline-block">
            <div className="flex items-center gap-3">
              <Avatar
                src={pendingTrainer.user.photo_url}
                name={pendingTrainer.user.first_name}
                size="lg"
                fallbackEmoji="👤"
              />
              <div className="text-left">
                <div className="font-medium text-tg-text">
                  {pendingTrainer.user.first_name}
                </div>
                <div className="text-sm text-orange-500">
                  Ожидает подтверждения
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : !trainer ? (
        // Нет тренера
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🏋️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">
            Добро пожаловать!
          </h2>
          <p className="text-tg-hint mb-6">
            Для записи на тренировки введите код приглашения от вашего тренера
          </p>
          <Link to="/join">
            <Button size="lg">
              Ввести код приглашения
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Карточка тренера */}
          <section className="mb-6">
            <div className="bg-tg-secondary rounded-xl p-4">
              <div className="flex items-center gap-4">
                <Avatar
                  src={trainer.user.photo_url}
                  name={`${trainer.user.first_name} ${trainer.user.last_name || ''}`}
                  size="xl"
                  fallbackEmoji="💪"
                />
                <div>
                  <div className="text-sm text-tg-hint">Ваш тренер</div>
                  <div className="font-semibold text-tg-text text-lg">
                    {trainer.user.first_name} {trainer.user.last_name || ''}
                  </div>
                  {trainer.specialization && (
                    <div className="text-sm text-tg-hint">{trainer.specialization}</div>
                  )}
                  {trainer.status === 'trial' && (
                    <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full mt-1">
                      Пробное занятие
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Ближайшие тренировки */}
          <section className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-tg-text">
                Ближайшие тренировки
              </h2>
              {upcomingBookings.length > 0 && (
                <Link to="/my-trainings" className="text-tg-button text-sm">
                  Все →
                </Link>
              )}
            </div>

            {upcomingBookings.length > 0 ? (
              <div className="space-y-3">
                {upcomingBookings.slice(0, 2).map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-tg-secondary rounded-xl p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">🏋️</div>
                      <div>
                        <div className="font-medium text-tg-text">
                          {formatBookingDate(booking)}
                        </div>
                        <div className="text-sm text-tg-hint">
                          {booking.slot.start_time} - {booking.slot.end_time}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-tg-secondary rounded-xl p-4 text-center">
                <p className="text-tg-hint">Нет запланированных тренировок</p>
              </div>
            )}
          </section>

          {/* Кнопка записи */}
          {trainer.status === 'trial' && trainer.trial_state === 'completed' ? (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
              <p className="font-medium text-orange-800">Пробная тренировка завершена</p>
              <p className="text-orange-600 text-sm mt-1">Ожидайте решения тренера</p>
            </div>
          ) : (
            <Link to="/booking">
              <Button fullWidth size="lg">
                {trainer.status === 'trial'
                  ? 'Записаться на пробную тренировку'
                  : 'Записаться на тренировку'}
              </Button>
            </Link>
          )}
        </>
      )}
    </div>
  )
}
